import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import { useArticlePropsStore } from "@core-ui/stores/ArticlePropsStore/ArticlePropsStore.provider";
import applyGroupLevels from "@ext/catalog/views/logic/utils/applyGroupLevels";
import putArticleIntoGroup, { type GroupLevel } from "@ext/catalog/views/logic/utils/putArticleIntoGroup";
import ErrorConfirmService from "@ext/errorHandlers/client/ErrorConfirmService";
import DefaultError from "@ext/errorHandlers/logic/DefaultError";
import { navigationTreeStore } from "@ext/navigation/catalog/SidebarNavigation/store/navigationTreeStore";
import type { CategoryLink, ItemLink, NavigationGroupLevel } from "@ext/navigation/NavigationLinks";
import PropertyServiceProvider from "@ext/properties/components/PropertyService";
import { moveArticleProperty } from "@ext/properties/logic/saveArticleProperties";
import { isManyProperty } from "@ext/properties/models";
import { useCallback } from "react";

const groupingOf = (link: ItemLink | undefined): NavigationGroupLevel[] => link?.options?.grouping ?? [];

const sourceGroupOf = (treeId: string): ItemLink | undefined => {
	const { flatIndex, parentMap } = navigationTreeStore.getState();
	return flatIndex[parentMap[treeId]];
};

const toLevels = (target: CategoryLink, treeId?: string): GroupLevel[] => {
	const source = treeId ? groupingOf(sourceGroupOf(treeId)) : [];

	return groupingOf(target).map(({ id, values }, level) => ({
		id,
		from: source[level]?.values[0] ?? null,
		to: values[0] ?? null,
	}));
};

/** FetchService already shows the server error of a non-ok response, so this one only unwinds the loop. */
class MoveRejected extends Error {}

const notifyMoveFailed = (error: unknown) => {
	if (error instanceof MoveRejected) return;
	ErrorConfirmService.notify(new DefaultError(error instanceof Error ? error.message : String(error)));
};

export const usePutArticleIntoGroup = () => {
	const apiUrlCreator = ApiUrlCreator.value;
	const { properties: catalogProperties } = PropertyServiceProvider.value;
	const { currentPath, currentProperties, updateArticleProps } = useArticlePropsStore(
		(state) => ({
			currentPath: state.data?.ref?.path,
			currentProperties: state.data?.properties,
			updateArticleProps: state.update,
		}),
		"shallow",
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	return useCallback(
		async (articlePath: string, group: CategoryLink, treeId?: string) => {
			const levels = toLevels(group, treeId);
			const store = navigationTreeStore.getState();
			const snapshot = treeId ? store.snapshotTree() : null;
			if (treeId) {
				store.setDragLocked(true);
				store.moveToGroup(treeId, group.ref.path);
			}

			try {
				await putArticleIntoGroup(articlePath, levels, async (path, property, from, to) => {
					const response = await moveArticleProperty(apiUrlCreator, path, property, from, to);
					if (!response.ok) throw new MoveRejected();
				});
			} catch (error) {
				if (snapshot) navigationTreeStore.getState().restoreTree(snapshot);
				notifyMoveFailed(error);
				return;
			} finally {
				if (treeId) navigationTreeStore.getState().setDragLocked(false);
			}

			if (articlePath === currentPath)
				updateArticleProps({
					properties: applyGroupLevels(
						currentProperties,
						levels,
						(id) => isManyProperty[catalogProperties.get(id)?.type],
					),
				});
		},
		[apiUrlCreator, catalogProperties, currentPath, currentProperties, updateArticleProps],
	);
};
